import React, { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../theme/theme';
import type { ThemeMode } from '../theme/theme';
import { ensureBrandedGoogleFontLoaded } from '../theme/googleFonts';
import { resolveBrandedGoogleFontName, resolveThemeTokens } from '../theme/resolveThemeTokens';
import { useAuth } from '../app/providers';
import type { ApiTheme } from '@/types/theme';

interface WebThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  systemPreference: 'light' | 'dark';
  /** Logo URL from dynamic theme (if set by entreprise) */
  logoUrl: string | null;
  /** Display title for logo text: theme.title > user.nameEntreprise > "Haveagooday" */
  displayTitle: string | null;
  /** Dynamic theme from API (entreprise branding) */
  apiTheme: ApiTheme | null;
}

const WebThemeContext = createContext<WebThemeContextType | undefined>(undefined);

interface WebThemeProviderProps {
  children: ReactNode;
}

export const WebThemeProvider: React.FC<WebThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [mode, setModeState] = useState<ThemeMode>('system' as ThemeMode);
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>('light');

  /** Dynamic theme from API (included in getUserInfo / hagd_user) */
  const apiTheme: ApiTheme | null = (user as { theme?: ApiTheme })?.theme ?? null;
  const logoUrl = apiTheme?.logo ?? null;
  /** Display title: theme.title > user.nameEntreprise > null (fallback to "Haveagooday" in UI) */
  const displayTitle =
    (apiTheme?.title?.trim() && apiTheme.title.trim()) ||
    (user as { nameEntreprise?: string })?.nameEntreprise ||
    null;

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemPreference = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    setSystemPreference(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', updateSystemPreference);

    return () => mediaQuery.removeEventListener('change', updateSystemPreference);
  }, []);

  // Load theme preference from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
    if (savedMode && ['light', 'dark', 'system'].includes(savedMode)) {
      setModeState(savedMode);
    } else {
      // Default to system preference
      setModeState('system' as ThemeMode);
    }
  }, []);

  const actualThemeMode = (mode === 'system' || mode === 'personal' ? systemPreference : mode) as 'light' | 'dark';

  useEffect(() => {
    ensureBrandedGoogleFontLoaded(resolveBrandedGoogleFontName(apiTheme));
  }, [apiTheme]);

  // Apply theme to document (MUI + CSS custom properties)
  useEffect(() => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light-theme', 'dark-theme');

    // Add new theme class
    root.classList.add(`${actualThemeMode}-theme`);

    // Set data attribute for CSS selectors
    root.setAttribute('data-theme', actualThemeMode);

    const { cssVars } = resolveThemeTokens(actualThemeMode, apiTheme);
    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [actualThemeMode, apiTheme]);

  // Save theme preference to localStorage
  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  const toggleMode = () => {
    const validModes: ThemeMode[] = ['light', 'dark', 'system']; // Exclude personal for now
    const currentIndex = validModes.indexOf(mode);
    const nextIndex = (currentIndex + 1) % validModes.length;
    setMode(validModes[nextIndex]);
  };

  const theme = createAppTheme(actualThemeMode, apiTheme);

  const value: WebThemeContextType = {
    mode,
    setMode,
    toggleMode,
    systemPreference,
    logoUrl,
    displayTitle,
    apiTheme,
  };

  return (
    <WebThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </WebThemeContext.Provider>
  );
};

// Export the context for use in the hook
export { WebThemeContext };
